import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import PostCard from "./PostCard";
import { MapPin, Loader2 } from "lucide-react";
import { SentimentLine, RiskBar } from "./charts";

/**
 * Shown when user clicks a row in Area Report or a map marker.
 * Shows posts from that state + mini stats + mini sentiment trend (via filter).
 */
export default function AreaDetailModal({ open, state, onClose, onOpenPost, onJumpToMap }) {
  const [posts, setPosts] = useState([]);
  const [total, setTotal] = useState(0);
  const [byCat, setByCat] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!state) return;
    setLoading(true);
    Promise.all([
      api.get(`/posts?state=${encodeURIComponent(state)}&limit=40`),
    ]).then(([r]) => {
      setPosts(r.data.items);
      setTotal(r.data.total);
      // compute per-category counts from fetched set
      const m = {};
      r.data.items.forEach((p) => { m[p.activity_category] = (m[p.activity_category] || 0) + 1; });
      setByCat(Object.entries(m).sort((a, b) => b[1] - a[1]));
    }).finally(() => setLoading(false));
  }, [state]);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] rounded-sm p-0 overflow-hidden" data-testid="area-modal">
        <DialogHeader className="px-5 py-4 border-b border-slate-200 bg-slate-50">
          <DialogTitle className="font-display text-lg font-semibold flex items-center gap-2">
            <MapPin className="h-4 w-4 text-[#2563eb]" />
            {state}
            <span className="text-xs font-mono text-slate-400">· {total} signals</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Regional breakdown of signals, categories, and narrative trajectory for {state}.
          </DialogDescription>
        </DialogHeader>

        <div className="p-5 grid grid-cols-12 gap-4 overflow-auto thin-scroll" style={{ maxHeight: "75vh" }}>
          <div className="col-span-12 lg:col-span-4 space-y-4">
            <div className="border border-slate-200 p-4">
              <div className="kicker mb-3">Category mix</div>
              <div className="space-y-2">
                {byCat.map(([cat, count]) => (
                  <div key={cat} className="text-xs">
                    <div className="flex items-center justify-between">
                      <span className="truncate pr-2">{cat}</span>
                      <span className="font-mono font-semibold">{count}</span>
                    </div>
                    <div className="h-1 mt-1 bg-slate-100">
                      <div
                        className="h-full bg-[#2563eb]"
                        style={{ width: `${Math.min(100, (count / Math.max(posts.length, 1)) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-8">
            <div className="kicker mb-2">Recent signals from {state}</div>
            {loading ? (
              <div className="p-8 flex items-center justify-center text-slate-500 text-sm">
                <Loader2 className="h-4 w-4 mr-2 animate-spin" /> loading…
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {posts.slice(0, 12).map((p) => (
                  <PostCard key={p.id} post={p} onClick={onOpenPost} onJumpToMap={onJumpToMap} compact />
                ))}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
